import { Controller } from "@hotwired/stimulus"

// Element palette. Which types are offered comes from the nesting rules in
// the schema, so a new node type needs no change here.
export default class extends Controller {
  static targets = ["list"]

  connect() {
    this.root = this.element.closest("[data-controller~='website-builder']")
    this.root.addEventListener("builder:ready", () => this.start(), { once: true })
    if (this.root.builder) this.start()
  }

  disconnect() { this.unsubscribe?.() }

  get builder() { return this.root.builder }
  get store() { return this.builder.store }

  start() {
    this.unsubscribe = this.store.subscribe((event) => {
      if (event.type === "select" || event.type === "document") this.render()
    })
    this.render()
  }

  // Where a click would insert: into the selection when it can hold the type,
  // otherwise the nearest ancestor that can, otherwise the last section on
  // the page. Dragging ignores this entirely -- the drop point decides.
  targetFor(type) {
    const schema = this.builder.schema
    let node = this.store.selected

    while (node) {
      if ((schema.children[node.type] || []).includes(type)) {
        return { parentId: node.id, index: (node.children || []).length }
      }
      const parent = this.store.parentOf(node.id)
      if (parent && (schema.children[parent.type] || []).includes(type)) {
        return { parentId: parent.id, index: this.store.indexOf(node.id) + 1 }
      }
      node = parent
    }

    const root = this.store.root
    if ((schema.children[root.type] || []).includes(type)) {
      return { parentId: root.id, index: (root.children || []).length }
    }

    // Nothing selected and the page itself will not take this type: fall back
    // to the last section, which is what a user means by "add a button".
    const sections = (root.children || []).filter((child) => (schema.children[child.type] || []).includes(type))
    const last = sections[sections.length - 1]
    if (last) return { parentId: last.id, index: (last.children || []).length }

    return null
  }

  render() {
    const schema = this.builder.schema
    this.listTarget.replaceChildren(
      ...schema.insertable.map((type) => {
        const button = document.createElement("button")
        button.type = "button"
        button.dataset.type = type
        button.dataset.action = "builder-palette#add"
        button.textContent = schema.labels.types[type] || type
        // Never disabled: a type with no click target is still draggable to a
        // valid drop point, and dimming it would make that undiscoverable.
        button.dataset.clickable = this.targetFor(type) === null ? "false" : "true"
        button.className =
          "cursor-grab touch-none select-none rounded-md border border-slate-200 px-2 py-1.5 " +
          "text-left text-xs font-medium hover:border-cyan-400 hover:bg-cyan-50 " +
          "data-[clickable=false]:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
        return button
      })
    )
  }

  async add(event) {
    const type = event.currentTarget.dataset.type
    const target = this.targetFor(type)
    if (!target) return

    await this.builder.createNode(type, target.parentId, target.index)
  }
}
